/**
 * Exercise Library Page
 *
 * Searchable, filterable list of all 60+ exercises.
 * Filter chips: muscle group, equipment, category.
 * Tap to see exercise detail: muscle allocations, best 1RM, chart, usage count.
 * Custom exercises show an Edit button.
 */

import { useState, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { calc1RM } from '@/hooks/use1RM';
import LineChart from '@/components/charts/LineChart';
import ExerciseCreator from '@/components/ExerciseCreator';
import type { Exercise, MuscleGroup, Equipment } from '@/types';

// ── Filter state ──────────────────────────────────────────────

type CategoryFilter = 'all' | 'compound' | 'isolation';

const MUSCLE_FILTER_OPTIONS: { value: MuscleGroup | 'all'; label: string }[] = [
  { value: 'all',        label: 'All Muscles' },
  { value: 'chest',      label: 'Chest' },
  { value: 'front_delt', label: 'Delts' },
  { value: 'lats',       label: 'Back' },
  { value: 'biceps',     label: 'Biceps' },
  { value: 'triceps',    label: 'Triceps' },
  { value: 'quads',      label: 'Quads' },
  { value: 'hamstrings', label: 'Hamstrings' },
  { value: 'glutes',     label: 'Glutes' },
  { value: 'core',       label: 'Core' },
];

const EQUIPMENT_FILTER_OPTIONS: { value: Equipment | 'all'; label: string }[] = [
  { value: 'all',        label: 'All' },
  { value: 'barbell',    label: 'Barbell' },
  { value: 'dumbbell',  label: 'Dumbbell' },
  { value: 'cable',     label: 'Cable' },
  { value: 'machine',   label: 'Machine' },
  { value: 'bodyweight', label: 'Bodyweight' },
];

const EQUIPMENT_ICONS: Partial<Record<Equipment, string>> = {
  barbell: '🏋️', dumbbell: '💪', cable: '🔗',
  machine: '⚙️', kettlebell: '🔔', bodyweight: '🤸',
  ez_bar: '〰️', bands: '🎗️', trap_bar: '⬡',
};

// ── Form cue lookup — all 115 built-in exercises ─────────────

const FORM_CUES: Record<string, string> = {
  // ── Chest / Horizontal Push ───────────────────────────────────
  ex_bp_barbell:           'Retract and depress scapula, maintain a slight arch. Bar path from nipple line toward upper chest. Drive feet into the floor throughout. Touch lightly, press explosively.',
  ex_bp_incline_barbell:   'Set bench 30–45°. Grip slightly narrower than flat. Touch upper chest. Keep elbows ~60° from torso — not flared wide.',
  ex_bp_db:                'Allow a full stretch at the bottom, strong pec squeeze at the top. Control the eccentric. Dumbbells travel in a slight arc, not straight up.',
  ex_bp_incline_db:        'Let dumbbells travel wide for maximum upper pec stretch. Squeeze hard at top. Slight internal rotation at peak to improve contraction.',
  ex_chest_fly_cable:      'Slight bend in elbows, hold throughout. Think about hugging a tree. Lead with your elbows, not your hands. Squeeze pecs hard at the midline.',
  ex_chest_fly_db:         'Arms wide, slight elbow bend, lower under control. Stop when you feel the stretch — don\'t strain. Bring up in an arc, squeezing at the top.',
  ex_pushup:               'Hands slightly wider than shoulders, body in a rigid plank. Lower chest to floor. Keep core tight. Elbows ~45° from torso.',
  ex_decline_bp_barbell:   'Decline emphasises lower chest. Feet secured. Grip normal width. Control the descent. Bar to lower chest.',
  ex_close_grip_bench:     'Grip shoulder-width — not too narrow. Keep elbows tucked. Triceps do the work; chest assists. Full lockout at top.',
  ex_incline_db_fly:       'Same arc motion as flat fly, but upper chest and front delt stretch more. Don\'t overload — prioritise the stretch and squeeze.',
  ex_cable_crossover_high_low: 'Cables set high, pull downward and across the midline. Lower chest emphasis. Squeeze at the cross point, hold one second.',
  ex_cable_crossover_low_high: 'Cables set low, pull upward and inward. Upper chest emphasis. Keep slight elbow bend. Squeeze and hold at the top.',
  ex_pec_deck:             'Set seat so elbows are at shoulder height. Lead with elbows, not forearms. Squeeze at full closure. Don\'t let weight crash back open.',
  ex_chest_press_machine:  'Adjust seat so handles are at mid-chest. Full range of motion. Press through the full arc of the machine. Control the return.',

  // ── Vertical Push / Shoulders ────────────────────────────────
  ex_ohp_barbell:          'Bar close to face on the way up — push your head through at lockout. Brace core hard, don\'t hyperextend lower back. Full lockout overhead.',
  ex_ohp_db:               'Neutral spine, slight lean. Press overhead to full lockout without flaring ribs. Control the descent to ear level.',
  ex_lateral_raise_db:     'Slight forward lean, lead with your elbow (not your hand). Stop at shoulder height. Don\'t shrug. Slow eccentric.',
  ex_lateral_raise_cable:  'Cable maintains tension at the bottom that dumbbells don\'t. Lean slightly away from the stack. Same elbow-led technique as dumbbell version.',
  ex_front_raise_db:       'Arms slightly soft at elbow. Raise to shoulder height — no higher needed. Control the descent. Don\'t swing with the body.',
  ex_cable_front_raise:    'Cable keeps constant tension. Hold at shoulder height for a moment. Use a light enough weight to isolate front delt without torso sway.',
  ex_arnold_press:         'Start with palms facing you (like top of a curl). Rotate outward as you press up. Reverse on the way down. Full shoulder ROM.',
  ex_upright_row_barbell:  'Narrow grip, pull to chin height. Lead with elbows. Careful with shoulder impingement — don\'t raise above chin.',
  ex_shoulder_press_machine: 'Adjust seat so handles are at shoulder height. Press smoothly through the full ROM. Don\'t arch the lower back.',
  ex_landmine_press:       'One arm at a time. Stand tall or stagger stance. Press in an arc, finishing with full elbow extension. Core braced throughout.',

  // ── Triceps ───────────────────────────────────────────────────
  ex_tri_pushdown_cable:   'Elbows pinned to sides — don\'t let them drift forward. Full extension at bottom. Squeeze triceps. Slow eccentric.',
  ex_tri_overhead_cable:   'Elbows stay by ears throughout. Full extension overhead, then resist fully on the way back. Long-head stretch is the goal here.',
  ex_skull_crusher:        'Lower the bar to your forehead or slightly behind. Keep upper arms perpendicular to floor. Extend without flaring elbows wide.',
  ex_dips:                 'Lean slightly forward for chest emphasis, stay upright for triceps. Lock out fully at top. Control the descent to ~90° elbow bend.',
  ex_tricep_machine:       'Adjust pad height. Keep upper arms stationary. Full extension, pause at bottom. Controlled return.',

  // ── Quads / Squat patterns ────────────────────────────────────
  ex_squat_barbell:        'Bar on upper traps (high bar) or rear delts (low bar). Chest up, knees track toes. Break hips and knees together. Drive through full foot.',
  ex_leg_press:            'Foot position changes emphasis — higher = more glutes/hamstrings, lower = more quads. Don\'t lock out knees. Control the return.',
  ex_leg_extension:        'Pause and squeeze at full extension. Don\'t use momentum to swing the weight up. Slow, controlled eccentric.',
  ex_lunge_db:             'Step far enough forward that your front shin stays vertical. Lower back knee toward the floor. Drive through the front heel to stand.',
  ex_hack_squat:           'Feet shoulder-width or slightly narrower. Full depth for maximum quad stretch. Push through quads, not just glutes.',
  ex_front_squat:          'Elbows high throughout — this keeps the torso upright. More quad-dominant than back squat. Clean grip or cross-arm grip.',
  ex_goblet_squat:         'Hold dumbbell/kettlebell at chest. Upright torso. Drive elbows between knees at the bottom. Great for learning squat pattern.',
  ex_bulgarian_split_squat: 'Rear foot elevated. Front foot far enough forward for vertical shin. Lower straight down. Drive through front heel. Crush for glutes + quads.',
  ex_reverse_lunge_db:     'Step back, lower rear knee toward ground. Front shin stays vertical. Return by driving through front foot. More stable than forward lunge.',
  ex_step_up_db:           'Full step — don\'t use the trailing leg to help. Drive through the heel of the working leg. Stand tall at the top.',
  ex_sumo_squat_db:        'Wide stance, toes out. Hold dumbbell between legs. Sit into the hips. Glutes and inner thighs do most of the work.',
  ex_sissy_squat:          'Hold for balance. Lean back as you lower, keeping hips extended. Extreme quad stretch — start with bodyweight only.',
  ex_smith_squat:          'Fixed bar path allows slight forward lean. Foot placement determines muscle emphasis. Control descent, drive up hard.',

  // ── Core ──────────────────────────────────────────────────────
  ex_ab_wheel:             'Brace hard before rolling out. Roll out only as far as you can maintain a neutral spine. Pull back using lats and abs together.',
  ex_cable_crunch:         'Rope at top pulley. Kneel, crunch down toward knees — don\'t pull with arms. Round the spine. Keep hips stationary.',
  ex_hanging_knee_raise:   'Dead hang. Raise knees to chest using abs, not hip flexors. Control the descent. Avoid swinging.',
  ex_hanging_leg_raise:    'Dead hang. Keep legs straight and raise to 90°+. Core does the work. If swinging is an issue, add a pause at the top.',
  ex_plank:                'Neutral spine — don\'t sag or pike. Squeeze glutes and quads. Breathe steadily. Push the floor away with your forearms.',
  ex_crunch_floor:         'Hands behind head without pulling on neck. Curl the ribcage toward the pelvis. Keep lower back on the floor throughout.',
  ex_bicycle_crunch:       'Slow and controlled — not a speed drill. Rotate elbow to opposite knee. Extend the other leg fully. Continuous tension.',
  ex_side_plank:           'Body in a straight line. Stack or stagger feet. Drive hips up, don\'t sag. Hold for time or add reps by dipping hips.',
  ex_pallof_press:         'Stand sideways to the cable. Press straight out and resist rotation — that\'s the point. Hold extended for 1–2 seconds. Anti-rotation is the focus.',
  ex_roman_chair_situp:    'Anchor feet, lower back slowly, come up fully. Don\'t hyperextend at the bottom. Core drives the return.',
  ex_ab_machine:           'Adjust for full ROM. Crunch down — don\'t just pull with arms. Squeeze at peak contraction. Slow on the way back.',
  ex_russian_twist:        'Lean back slightly, feet up or grounded. Rotate fully side to side, touching weight to floor. Control — not a race.',

  // ── Glutes / Hip ──────────────────────────────────────────────
  ex_glute_bridge_bw:      'Shoulder blades on floor, knees bent. Drive through heels. Squeeze glutes hard at the top. Chin tucked. Hold briefly.',
  ex_hip_abduction_machine: 'Sit tall. Push legs apart against resistance. Squeeze at full abduction. Slow controlled return. Targets glute med.',
  ex_hip_adduction_machine: 'Sit tall. Squeeze legs together. Hold at the inner position. Slow return. Targets inner thigh and glute fibers.',
  ex_hip_thrust:           'Shoulder blades on bench edge, bar padded at hip crease. Drive through heels. Chin tucked. Max glute squeeze at full hip extension.',
  ex_cable_glute_kickback: 'Ankle attachment. Hinge slightly forward. Kick leg straight back and up — don\'t rotate the hip. Squeeze glute at peak.',
  ex_glute_kickback_machine: 'Adjust pad behind knee. Drive leg back and up. Peak contraction at full hip extension. Don\'t arch lower back excessively.',

  // ── Hip Hinge / Hamstrings ────────────────────────────────────
  ex_deadlift:             'Bar over mid-foot, hinge to grip. Pull slack out before driving. Neutral spine throughout. Push the floor away — don\'t just pull up.',
  ex_rdl:                  'Soft knee bend. Push hips back as bar travels down. Feel hamstring stretch. Brace core. Bar stays close to legs the whole way.',
  ex_rdl_db:               'Same hip-hinge pattern as barbell RDL. Dumbbells travel in front of legs. Stretch until you feel the hamstrings load, then squeeze back up.',
  ex_sumo_deadlift:        'Wide stance, toes out, grip inside legs. Hips drop lower than conventional. Bar stays close. Push floor apart as you drive up.',
  ex_trap_bar_deadlift:    'Stand in the center of the trap bar. More quad-friendly than conventional. Stand tall at lockout. Great for athletes or those with back issues.',
  ex_good_morning:         'Bar on upper back. Hinge forward with soft knees. Feel hamstring tension at bottom. Brace core hard. Don\'t round the lower back.',
  ex_cable_pull_through:   'Straddle the cable, hinge forward, pull through to full hip extension. Glutes do the work, not your arms. Similar motor pattern to deadlift.',
  ex_nordic_hamstring_curl: 'Anchor feet. Lower torso toward floor as slowly as possible. This is very demanding — use a band for assistance when starting.',
  ex_glute_ham_raise:      'Start parallel to floor. Lower slowly under hamstring control. Drive back up using hamstrings and glutes. Excellent posterior chain exercise.',
  ex_smith_rdl:            'Fixed bar path helps with balance. Same hip-hinge cues as barbell RDL. Hamstring stretch at bottom, squeeze glutes on the way up.',
  ex_leg_curl_lying:       'Keep hips pressed into the pad. Full extension at the bottom — resist it. Curl to full flexion, squeeze at the top.',
  ex_leg_curl_seated:      'Seated version gives a better hamstring stretch. Full range of motion. Pause and squeeze at peak contraction.',

  // ── Calves ────────────────────────────────────────────────────
  ex_calf_raise_standing:  'Full stretch at bottom — don\'t cheat the range. Slow eccentric (3 seconds down). Pause at the top. Most people go too fast.',
  ex_calf_raise_seated:    'Seated calf raise targets soleus (under the gastrocnemius). Same principles — full stretch, slow eccentric, peak contraction.',
  ex_calf_raise_bw:        'Use a step for full ROM. Single-leg is harder and more effective. Full stretch at the bottom is the key.',
  ex_calf_raise_leg_press: 'Use the balls of your feet on the platform edge. Full ROM. Don\'t lock out knees. Slow and controlled throughout.',

  // ── Vertical Pull / Back ──────────────────────────────────────
  ex_pullup:               'Dead hang start. Depress scapula first, then pull. Chin over bar. Controlled descent to a full hang. Avoid kipping for hypertrophy.',
  ex_chinup:               'Supinated (underhand) grip. More bicep involvement than pull-up. Squeeze biceps and lats simultaneously at the top.',
  ex_neutral_grip_pullup:  'Hands facing each other. Comfortable on elbows/wrists. Strong lat activation. Great alternative if pronated grip causes discomfort.',
  ex_assisted_pullup_machine: 'Set the counterweight appropriately. Same technique as unassisted pull-up. Use it to build toward bodyweight.',
  ex_lat_pulldown:         'Slight lean back, grip just wider than shoulder-width. Pull bar to upper chest, driving elbows down and back. Stretch fully at the top.',
  ex_pulldown_machine:     'Adjust seat and handles. Pull to upper chest, elbows travel down. Stretch lats fully at the top. Control the return.',
  ex_straight_arm_pulldown: 'Arms straight throughout. Hinge at shoulder only. Pull rope or bar down to hips. Full lat stretch at the top. Light weight — pure isolation.',
  ex_db_pullover:          'Lie on bench, hold one dumbbell overhead. Lower behind head with straight arms. Stretch lats and chest. Pull back up in an arc.',

  // ── Horizontal Pull / Rows ────────────────────────────────────
  ex_row_barbell:          'Hip hinge ~45°. Pull bar to lower stomach, lead with elbows. Retract and depress scapula at the top. Lower with control.',
  ex_row_db:               'Brace with non-working arm on bench. Pull dumbbell to hip, elbow close to body. Full stretch at the bottom — let shoulder protract.',
  ex_row_cable_seated:     'Tall posture, full stretch forward. Pull to navel. Retract scapula fully at the finish. Don\'t rock the torso.',
  ex_chest_supported_row:  'Chest stays on pad throughout — eliminates lower back stress. Focus on retracting scapula. Let shoulder protract forward on the stretch.',
  ex_tbar_row:             'Straddle the bar. Hip hinge position. Pull to chest, lead with elbows. Great for thickness. Control the negative.',
  ex_pendlay_row:          'Bar starts on the floor each rep — reduces cheating. Fully horizontal torso. Explosive pull, controlled descent back to floor.',
  ex_meadows_row:          'Staggered stance, landmine-style. Pull to hip. Tremendous lat stretch at bottom. Go heavier than you think.',
  ex_landmine_row:         'Similar to meadows row. Great for those with lower back issues — less spinal load. Full ROM and strong retraction at top.',
  ex_row_machine:          'Adjust seat/chest pad. Full stretch, full retraction. Let the scapula move freely. Don\'t just use arms — engage the back.',

  // ── Face Pulls / Upper Back ───────────────────────────────────
  ex_face_pull:            'Elbows high and wide — above shoulder height. Pull to forehead, externally rotate at the end. Rear delts and rotator cuff health.',

  // ── Rear Delts ────────────────────────────────────────────────
  ex_rear_delt_fly_db:     'Lean forward to ~45°. Arms slightly bent. Raise to shoulder height, squeezing rear delts and rhomboids. Light weight, feel the contraction.',
  ex_cable_rear_delt_fly:  'Cable at head height. Pull across body. Same cues as dumbbell rear delt fly — light weight, full contraction.',
  ex_rear_delt_machine:    'Set pads appropriately. Drive elbows back and outward. Squeeze rear delts at full range. Don\'t use momentum.',

  // ── Traps ─────────────────────────────────────────────────────
  ex_shrug_barbell:        'Straight arms. Shrug straight up — don\'t roll shoulders. Hold at the top for a second. Slow descent.',
  ex_shrug_db:             'Same technique as barbell shrug. Dumbbells allow arms to hang naturally. Squeeze at the top.',

  // ── Biceps ────────────────────────────────────────────────────
  ex_curl_barbell:         'No swinging. Supinate fully at the top. Control the descent. Full stretch at the bottom — don\'t cut it short.',
  ex_curl_db_incline:      'Incline position increases the stretch on the long head. No momentum whatsoever. Full supination at the contraction.',
  ex_curl_cable:           'Cable keeps constant tension at the bottom. Curl to full flexion, squeeze, lower with control.',
  ex_curl_db_standing:     'Alternate or simultaneous. Supinate as you curl. Control the negative. Don\'t let the wrist flex — neutral wrist.',
  ex_curl_ezbar:           'EZ-bar is easier on wrists than straight bar. Same principles — no swing, full ROM, controlled eccentric.',
  ex_hammer_curl:          'Neutral grip throughout. Targets brachialis and brachioradialis significantly more than biceps. Great for arm thickness.',
  ex_preacher_curl:        'Arm against the pad throughout — no cheating. Full extension at the bottom (don\'t lock out). Squeeze hard at the top.',
  ex_concentration_curl:   'Elbow braced against inner thigh. Pure bicep isolation. Supinate fully at the top. Slow and deliberate.',
  ex_spider_curl:          'Lying face-down on incline bench. Hanging arms — gravity provides constant tension. Very strict — no body sway possible.',
  ex_seated_db_curl:       'Sitting removes temptation to use hip drive. Alternate arms. Supinate fully. Control both the up and down phase.',
  ex_bicep_curl_machine:   'Adjust seat so elbows rest at pivot point. Full ROM. Squeeze at the top. Slow eccentric.',

  // ── Forearms ──────────────────────────────────────────────────
  ex_reverse_curl:         'Pronated (overhand) grip. Curl to full flexion. Targets brachioradialis and forearm extensors heavily. Light weight is fine.',
  ex_zottman_curl:         'Curl up with supinated grip, rotate to pronated at the top, lower with pronated grip. Works biceps on the way up, forearms on the way down.',
  ex_wrist_curl:           'Forearm on bench, wrist hanging off edge. Curl the wrist up fully. Slow eccentric. Great for forearm flexors and grip.',
  ex_reverse_wrist_curl:   'Pronated version. Wrist extends upward. Targets forearm extensors. Keep the rep range higher — these are smaller muscles.',

  // ── Carries / Functional ──────────────────────────────────────
  ex_farmers_carry:        'Tall posture, shoulders back, core braced. Small, controlled steps. Grip is often the limiting factor. Great for total-body strength and stability.',
};


// ── Exercise detail sheet ─────────────────────────────────────

function ExerciseDetail({
  exercise,
  onClose,
  onEdit,
}: {
  exercise: Exercise;
  onClose: () => void;
  onEdit: () => void;
}) {
  const history = useLiveQuery(async () => {
    const sets = await db.loggedSets
      .where('exerciseId').equals(exercise.id)
      .filter((s) => !s.isWarmup)
      .sortBy('loggedAt');

    const byDate = new Map<string, number>();
    for (const s of sets) {
      const date = s.loggedAt.slice(0, 10);
      byDate.set(date, Math.max(byDate.get(date) ?? 0, calc1RM(s.weightLbs, s.reps)));
    }
    return Array.from(byDate.entries())
      .map(([date, value]) => ({ date, value: Math.round(value * 10) / 10 }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [exercise.id]) ?? [];

  const usageCount = useLiveQuery(
    () => db.loggedSets.where('exerciseId').equals(exercise.id).count(),
    [exercise.id],
  ) ?? 0;

  const bestPR = history.length > 0
    ? Math.max(...history.map((d) => d.value))
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-safe">
      <button className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-surface-100 rounded-t-3xl
                      pt-4 pb-6 shadow-card-lg animate-slide-up
                      max-h-[85vh] flex flex-col">
        <div className="w-10 h-1 bg-surface-400 rounded-full mx-auto mb-4 flex-shrink-0" />

        <div className="flex items-start justify-between px-6 mb-4 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-3">
            <div className="flex gap-2 flex-wrap mb-1">
              <span className={`badge text-xs ${exercise.category === 'compound' ? 'badge-accent' : 'bg-surface-400 text-ink-muted'}`}>
                {exercise.category}
              </span>
              {!exercise.isBuiltIn && (
                <span className="badge bg-success/20 text-success text-xs">Custom</span>
              )}
            </div>
            <h3 className="font-bold text-ink text-lg leading-tight">{exercise.name}</h3>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {!exercise.isBuiltIn && (
              <button className="btn-ghost text-sm px-3 py-1.5" onClick={onEdit}>Edit</button>
            )}
            <button className="btn-ghost p-1 text-lg" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 space-y-4 no-scrollbar">
          {/* Stats */}
          <div className="flex gap-3">
            {[
              { label: 'Best 1RM', value: bestPR > 0 ? `${Math.round(bestPR)} lbs` : '—' },
              { label: 'Sets Logged', value: String(usageCount) },
              { label: 'Data Points', value: String(history.length) },
            ].map(({ label, value }) => (
              <div key={label} className="flex-1 card text-center py-2.5">
                <p className="font-bold text-ink text-base">{value}</p>
                <p className="text-xs text-ink-muted mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* 1RM chart */}
          {history.length >= 2 && (
            <div className="card">
              <LineChart data={history} unit="lbs" color="#7c3aed" height={120} />
            </div>
          )}

          {/* Muscle allocations */}
          <div className="card space-y-2">
            <p className="text-xs text-ink-muted font-medium uppercase tracking-widest">
              Muscle Allocation
            </p>
            {exercise.muscles.map((alloc) => (
              <div key={alloc.muscle} className="flex items-center gap-3">
                <div className="flex-1 h-2 bg-surface-400 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent rounded-full"
                    style={{ width: `${alloc.fraction * 100}%` }}
                  />
                </div>
                <span className="text-sm text-ink w-28 text-right capitalize">
                  {alloc.muscle.replace('_', ' ')}
                </span>
                <span className="text-xs text-ink-faint w-8 text-right">
                  {alloc.fraction}×
                </span>
              </div>
            ))}
          </div>

          {/* Form cue */}
          {(FORM_CUES[exercise.id] || exercise.formCue) && (
            <div className="bg-accent/8 rounded-2xl px-4 py-3.5 border border-accent/15">
              <p className="text-xs text-accent uppercase tracking-widest font-semibold mb-1.5">
                Form Cue
              </p>
              <p className="text-sm text-ink leading-relaxed">
                {exercise.formCue ?? FORM_CUES[exercise.id]}
              </p>
            </div>
          )}

          {/* Equipment */}
          <div className="flex flex-wrap gap-2">
            {exercise.equipment.map((eq) => (
              <span key={eq} className="badge bg-surface-300 text-ink-muted text-xs">
                {EQUIPMENT_ICONS[eq]} {eq}
              </span>
            ))}
          </div>

          {/* User notes */}
          {exercise.notes && (
            <div className="bg-surface-200 rounded-2xl px-4 py-3">
              <p className="text-xs text-ink-faint uppercase tracking-widest mb-1">Notes</p>
              <p className="text-sm text-ink-muted">{exercise.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────

export default function ExerciseLibraryPage() {
  const [search, setSearch]         = useState('');
  const [muscleFilter, setMuscle]   = useState<MuscleGroup | 'all'>('all');
  const [equipFilter, setEquip]     = useState<Equipment | 'all'>('all');
  const [catFilter, setCat]         = useState<CategoryFilter>('all');
  const [selectedEx, setSelectedEx] = useState<Exercise | null>(null);
  const [showCreator, setShowCreator] = useState(false);
  const [editingEx, setEditingEx]   = useState<Exercise | null>(null);

  const allExercises = useLiveQuery(() =>
    db.exercises.orderBy('name').toArray(), []
  ) ?? [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return allExercises.filter((ex) => {
      if (q && !ex.name.toLowerCase().includes(q)) return false;
      if (muscleFilter !== 'all' && !ex.muscles.some((m) => m.muscle === muscleFilter)) return false;
      if (equipFilter !== 'all' && !ex.equipment.includes(equipFilter)) return false;
      if (catFilter !== 'all' && ex.category !== catFilter) return false;
      return true;
    });
  }, [allExercises, search, muscleFilter, equipFilter, catFilter]);

  const customCount = allExercises.filter((e) => !e.isBuiltIn).length;

  return (
    <div className="content-pad-bottom animate-fade-in">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-surface/95 backdrop-blur-xl px-4 pt-6 pb-3
                      border-b border-surface-400">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold text-ink tracking-tight">Exercise Library</h1>
            <p className="text-xs text-ink-muted">
              {filtered.length} of {allExercises.length} exercises
              {customCount > 0 && ` · ${customCount} custom`}
            </p>
          </div>
          <button
            className="btn-primary text-sm px-4 py-2"
            onClick={() => setShowCreator(true)}
          >
            + New
          </button>
        </div>

        {/* Search */}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search exercises…"
          className="w-full bg-surface-300 rounded-xl px-4 py-3 text-sm text-ink
                     focus:outline-none focus:ring-2 focus:ring-accent
                     placeholder:text-ink-faint mb-3"
        />

        {/* Filter chips — scrollable rows */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-1">
          {MUSCLE_FILTER_OPTIONS.map(({ value, label }) => (
            <button key={value}
              onClick={() => setMuscle(value)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium
                          transition-colors min-h-tap
                ${muscleFilter === value ? 'bg-accent text-white' : 'bg-surface-300 text-ink-muted'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {EQUIPMENT_FILTER_OPTIONS.map(({ value, label }) => (
            <button key={value}
              onClick={() => setEquip(value)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium
                          transition-colors min-h-tap
                ${equipFilter === value ? 'bg-accent text-white' : 'bg-surface-300 text-ink-muted'}`}>
              {label}
            </button>
          ))}
          {(['all','compound','isolation'] as CategoryFilter[]).map((c) => (
            <button key={c}
              onClick={() => setCat(c)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium
                          transition-colors min-h-tap
                ${catFilter === c ? 'bg-success text-white' : 'bg-surface-300 text-ink-muted'}`}>
              {c === 'all' ? 'All Types' : c.charAt(0).toUpperCase() + c.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Exercise list */}
      <div className="px-4 py-3 space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-ink-muted text-sm">No exercises match your filters</p>
            <button className="btn-ghost text-sm mt-3 text-accent"
              onClick={() => { setSearch(''); setMuscle('all'); setEquip('all'); setCat('all'); }}>
              Clear filters
            </button>
          </div>
        ) : (
          filtered.map((ex) => (
            <button
              key={ex.id}
              className="w-full card flex items-center gap-3 text-left
                         hover:bg-surface-200 active:scale-[0.98] transition-all duration-150"
              onClick={() => setSelectedEx(ex)}
            >
              {/* Category badge */}
              <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center
                               justify-center text-xs font-bold
                               ${ex.category === 'compound'
                                 ? 'bg-accent/20 text-accent'
                                 : 'bg-surface-400 text-ink-muted'}`}>
                {ex.category === 'compound' ? 'C' : 'I'}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-ink text-sm truncate">{ex.name}</p>
                  {!ex.isBuiltIn && (
                    <span className="badge bg-success/20 text-success text-xs flex-shrink-0">
                      Custom
                    </span>
                  )}
                </div>
                <p className="text-xs text-ink-muted mt-0.5">
                  {ex.muscles[0]?.muscle.replace('_', ' ')} ·{' '}
                  {ex.equipment.slice(0, 2).map((e) => EQUIPMENT_ICONS[e] ?? e).join(' ')}
                </p>
              </div>

              <span className="text-ink-faint text-sm flex-shrink-0">›</span>
            </button>
          ))
        )}
      </div>

      {/* Modals */}
      {selectedEx && (
        <ExerciseDetail
          exercise={selectedEx}
          onClose={() => setSelectedEx(null)}
          onEdit={() => { setEditingEx(selectedEx); setSelectedEx(null); }}
        />
      )}

      {(showCreator || editingEx) && (
        <ExerciseCreator
          onClose={() => { setShowCreator(false); setEditingEx(null); }}
          onSaved={() => { setShowCreator(false); setEditingEx(null); }}
        />
      )}
    </div>
  );
}
